/**
 * Main Application Component
 */
import { RouterProvider } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, CssBaseline, GlobalStyles } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { arEG, enUS } from '@mui/material/locale';
import { Toaster } from 'react-hot-toast';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { store, useAppSelector } from './store';
import { lightTheme, darkTheme } from './theme';
import { router } from './router';
import { ErrorBoundary } from './components/common';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/ar';
import './i18n/config';

// Emotion caches: the RTL one flips physical CSS (margins/paddings) for Arabic.
const cacheLtr = createCache({ key: 'mui', stylisPlugins: [prefixer] });
const cacheRtl = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });

// Theme wrapper that uses Redux state
const ThemedApp = () => {
  const { themeMode, language } = useAppSelector((state) => state.ui);
  const { i18n } = useTranslation();
  const direction = language === 'ar' ? 'rtl' : 'ltr';
  const baseTheme = themeMode === 'dark' ? darkTheme : lightTheme;

  // Set moment's locale during render, not in an effect: every `moment().format()`
  // in the tree below reads this global, and an effect would run only after those
  // children have already formatted their dates with the previous locale.
  moment.locale(language === 'ar' ? 'ar' : 'en');

  // Create theme with direction
  const theme = createTheme(
    {
      ...baseTheme,
      direction: direction as 'rtl' | 'ltr',
    },
    // Built-in MUI texts (e.g. "Rows per page") in the dashboard language
    language === 'ar' ? arEG : enUS,
  );

  // Sync i18n language with Redux state
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  // Update document direction for RTL languages
  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
  }, [language, direction]);

  return (
    <CacheProvider value={direction === 'rtl' ? cacheRtl : cacheLtr}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Flip directional icons (back/forward/chevrons) in RTL — MUI icons don't auto-mirror. */}
      <GlobalStyles
        styles={{
          '[dir="rtl"] [data-testid$="ArrowBackIcon"], [dir="rtl"] [data-testid$="ArrowForwardIcon"], [dir="rtl"] [data-testid$="ArrowBackIosIcon"], [dir="rtl"] [data-testid$="ArrowForwardIosIcon"], [dir="rtl"] [data-testid$="ChevronLeftIcon"], [dir="rtl"] [data-testid$="ChevronRightIcon"], [dir="rtl"] [data-testid$="KeyboardArrowLeftIcon"], [dir="rtl"] [data-testid$="KeyboardArrowRightIcon"], [dir="rtl"] [data-testid$="NavigateBeforeIcon"], [dir="rtl"] [data-testid$="NavigateNextIcon"], [dir="rtl"] [data-testid$="KeyboardBackspaceIcon"]':
            {
              transform: 'scaleX(-1)',
            },
        }}
      />
      <ErrorBoundary>
        <RouterProvider router={router} />
      </ErrorBoundary>
      <Toaster
        position={direction === 'rtl' ? 'top-left' : 'top-right'}
        toastOptions={{
          duration: 4000,
          style: {
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.divider}`,
          },
          success: {
            iconTheme: {
              primary: theme.palette.success.main,
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: theme.palette.error.main,
              secondary: '#fff',
            },
          },
        }}
      />
    </ThemeProvider>
    </CacheProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemedApp />
    </Provider>
  );
}

export default App;
